import React, { useMemo } from 'react';
import {
  TrendingUp, TrendingDown, CircleDot, Minus, Coins,
  DollarSign, Target, BrainCircuit, Ban, Eye, ChevronDown
} from 'lucide-react';
import { LLMDecision } from '../../types/api';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { DecisionCard } from './DecisionCard';
import { cn } from '../../lib/utils';

interface TradingDecisionCardProps {
  decision: LLMDecision;
  isLatest: boolean;
}

export const TradingDecisionCard: React.FC<TradingDecisionCardProps> = ({
  decision,
  isLatest,
}) => {
  const rec = decision.recommendation;

  // Memoize JSON stringification
  const jsonData = useMemo(() => {
    return JSON.stringify(
      { ...rec, _prompt_used: undefined },
      null,
      2
    );
  }, [rec]);

  const action = rec?.action;

  return (
    <DecisionCard decision={decision} isLatest={isLatest}>
      <CardContent className="space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Action Card */}
          <Card className={cn(
            "bg-gradient-to-br",
            action === 'open_position'
              ? 'from-green-50 to-green-100/50 dark:from-green-950/20 dark:to-green-900/10'
              : action === 'close_position'
              ? 'from-red-50 to-red-100/50 dark:from-red-950/20 dark:to-red-900/10'
              : action === 'hold'
              ? 'from-blue-50 to-blue-100/50 dark:from-blue-950/20 dark:to-blue-900/10'
              : 'from-gray-50 to-gray-100/50 dark:from-gray-950/20 dark:to-gray-900/10'
          )}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                {action === 'open_position' ? (
                  <TrendingUp className="h-3 w-3 text-green-600" />
                ) : action === 'close_position' ? (
                  <TrendingDown className="h-3 w-3 text-red-600" />
                ) : action === 'hold' ? (
                  <CircleDot className="h-3 w-3 text-blue-600" />
                ) : (
                  <Minus className="h-3 w-3 text-gray-600" />
                )}
                <span className={cn(
                  "text-xs font-medium",
                  action === 'open_position' ? 'text-green-600' :
                  action === 'close_position' ? 'text-red-600' :
                  action === 'hold' ? 'text-blue-600' : 'text-gray-600'
                )}>Decision</span>
              </div>
              <p className="text-sm font-bold capitalize">
                {action?.replace(/_/g, ' ') || 'N/A'}
              </p>
            </CardContent>
          </Card>

          {/* Symbol Card */}
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/20 dark:to-purple-900/10">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Coins className="h-3 w-3 text-purple-600" />
                <span className="text-xs font-medium text-purple-600">Symbol</span>
              </div>
              <p className="text-sm font-bold">{rec?.symbol || 'None'}</p>
            </CardContent>
          </Card>

          {/* Capital Card */}
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/20 dark:to-amber-900/10">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-3 w-3 text-amber-600" />
                <span className="text-xs font-medium text-amber-600">Capital</span>
              </div>
              <p className="text-sm font-bold">
                {rec?.capital_percentage || rec?.allocation_percentage || 0}%
              </p>
            </CardContent>
          </Card>

          {/* Price Card */}
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100/50 dark:from-cyan-950/20 dark:to-cyan-900/10">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-3 w-3 text-cyan-600" />
                <span className="text-xs font-medium text-cyan-600">Entry Price</span>
              </div>
              <p className="text-sm font-bold">
                {rec?.price ? `$${Number(rec.price).toLocaleString()}` : 'N/A'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* AI Summary */}
        {(rec?.details || rec?.reasoning) && (
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-2 text-sm">
              <BrainCircuit className="h-3 w-3" />
              AI Analysis Summary
            </h4>
            <Card className="bg-muted/30">
              <CardContent className="p-3">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {rec?.details || rec?.reasoning}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Rejected Pairs */}
        {rec?.rejected_pairs && rec.rejected_pairs.length > 0 && (
          <div>
            <h4 className="font-medium mb-2 flex items-center gap-2 text-sm">
              <Ban className="h-3 w-3 text-orange-500" />
              Rejected Pairs ({rec.rejected_pairs.length})
            </h4>
            <div className="space-y-2">
              {rec.rejected_pairs.map((pair: { symbol: string; reason: string }, idx: number) => (
                <Card key={idx} className="bg-orange-50/50 dark:bg-orange-950/10 border-orange-200/50 dark:border-orange-800/30">
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <Badge variant="outline" className="shrink-0 text-xs font-mono">
                        {pair.symbol}
                      </Badge>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {pair.reason}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Full Data - Collapsible */}
        <details className="cursor-pointer group">
          <summary className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <Eye className="h-3 w-3" />
            <span>Show Full Decision Data</span>
            <ChevronDown className="h-3 w-3 transition-transform group-open:rotate-180" />
          </summary>
          <Card className="bg-muted/30 mt-2">
            <CardContent className="p-3">
              <div className="max-h-64 overflow-y-auto">
                <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                  {jsonData}
                </pre>
              </div>
            </CardContent>
          </Card>
        </details>
      </CardContent>
    </DecisionCard>
  );
};

export default TradingDecisionCard;
